// components/forms/LoginForm.js
import React, { useState, useEffect, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import NeumorphicInput from '../../../../assets/components/auth/NeumorphicInput';
import NeumorphicButtonLong from '../../../../assets/components/auth/NeumorphicButtonLong';
import NeumorphicButtonWithIcon from '../../../../assets/components/auth/NeumorphicButtonWithIcon';
import NeumorphicDivider from '../../../../assets/components/auth/NeumorphicDivider';
import MicrosoftLogo from '../../../../assets/logos/others/MicrosoftLogo';
import GoogleLogo from '../../../../assets/logos/others/GoogleLogo';
import { useAuth } from '../../hooks/useAuth';
import { validators } from '../../utils/validators';

const LoginForm = memo(({ onSubmit, onOrgSuspended }) => {
  const location = useLocation();
  const { generateOtp, loginWithMicrosoft, loginWithGoogle, loading, error, clearError } = useAuth();
  
  const [formData, setFormData] = useState({
    email: ''
  });
  const [localError, setLocalError] = useState('');
  const isSubmittingRef = useRef(false);


  useEffect(() => {
    // Don't render if we're on callback URL
    if (window.location.search.includes('code=')) {
      return;
    }

    // Handle errors from location state
    if (location.state?.error) {
      setLocalError(location.state.error);
      window.history.replaceState({}, document.title);
    }

    // Check for provider auth failures from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const msFailed = urlParams.get('ms_failed') || urlParams.get('microsoft_failed');
    const googleFailed = urlParams.get('google_failed');
    
    if (msFailed === 'true') {
      setLocalError('Microsoft authentication failed. The next login attempt will show a fresh Microsoft login screen.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    if (googleFailed === 'true') {
      setLocalError('Google authentication failed. The next login attempt will show a fresh Google login screen.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    
    // Check for error messages in session storage
    const msErrorMessage = sessionStorage.getItem('ms_error_message');
    if (msErrorMessage) {
      setLocalError(msErrorMessage);
      sessionStorage.removeItem('ms_error_message');
    }

    const googleErrorMessage = sessionStorage.getItem('google_error_message');
    if (googleErrorMessage) {
      setLocalError(googleErrorMessage);
      sessionStorage.removeItem('google_error_message');
    }

    // Clean up stale auth state
    if (!window.location.search.includes('code=')) {
      // Clean up any restart flags
      sessionStorage.removeItem('restart_ms_auth_after_logout');
      sessionStorage.removeItem('ms_auth_email_retry');
      
      // Clean up processed codes
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('processed_code_')) {
          sessionStorage.removeItem(key);
        }
      });
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (localError) setLocalError('');
    if (error) clearError();
  };

  const handleEmailLogin = async () => {
  if (loading || isSubmittingRef.current) return;
  isSubmittingRef.current = true;

    if (!formData.email) {
      setLocalError('Email address is required');
      isSubmittingRef.current = false;
      return;
    }
    if (!formData.email) {
      setLocalError('Email address is required');
      isSubmittingRef.current = false;
      return;
    }

    if (!validators.isValidEmail(formData.email)) {
      setLocalError('Please enter a valid email address');
      isSubmittingRef.current = false;
      return;
    }
    if (!validators.isValidEmail(formData.email)) {
      setLocalError('Please enter a valid email address');
      isSubmittingRef.current = false;
      return;
    }

  try {
    const result = await generateOtp(formData.email);
    if (result.success && !result.byocRedirect) {
      onSubmit(formData);
    } else if (result.orgSuspended) {
      onOrgSuspended?.({
        organisationName: result.organisationName,
        suspendedAt: result.suspendedAt
      });
      isSubmittingRef.current = false;
    } else if (!result.success) {
      setLocalError(result.error);
      isSubmittingRef.current = false;
    }
    // byocRedirect: browser is navigating away, keep ref locked
  } catch {
    isSubmittingRef.current = false;
  }
};

  const handleMicrosoftLogin = () => {
    clearError();
    setLocalError('');
    loginWithMicrosoft(formData.email);
  };

  const handleGoogleLogin = () => {
    clearError();
    setLocalError('');
    loginWithGoogle(formData.email);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEmailLogin();
    }
  };

  // Don't render if we're on callback URL
  if (window.location.search.includes('code=')) {
    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        textAlign: 'center',
        color: 'transparent'
      }}>
        <p>Processing authentication...</p>
      </div>
    );
  }

  const displayError = localError || error;

  return (
    <div className="login-form-container">
      <h2 className="login-form-title login-heading">Welcome</h2>
      
    <form className="login-form" onSubmit={(e) => { e.preventDefault(); }}>
        <div className="login-form-group">
          <NeumorphicInput
            type="text"
            id="email"
            name="email"
            className="login-form-input"
            placeholder="Enter Email"
            value={formData.email}
            onChange={handleChange}
            disabled={loading}
          />
        </div>

        {displayError && (
          <div className="error-message" style={{ 
            color: 'red', 
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '10px'
          }}>
            {displayError}
          </div>
        )}
        
        <NeumorphicButtonLong
          type="button"
          onClick={handleEmailLogin}
          isLoading={loading}
          disabled={loading}
          hoverColor="#9404C3"
          textColor="#9404C3"
          className='login-form-button'
        >
          {loading ? 'Verifying...' : 'Log In'}
        </NeumorphicButtonLong>

        <div className="login-hero-text" style={{ marginTop: '5px', marginBottom: '-10px' }}>
          <span>OR</span>
        </div>

        <NeumorphicButtonWithIcon 
          type="button" 
          icon={<MicrosoftLogo/>}
          isLoading={loading}
          disabled={loading}
          onClick={handleMicrosoftLogin}
          hoverColor="#9404C3"
          data-microsoft-login="true"
        >
          {loading ? 'Redirecting...' : 'Continue with Microsoft'}
        </NeumorphicButtonWithIcon>

        {/* <NeumorphicButtonWithIcon 
          type="button" 
          icon={<GoogleLogo/>}
          isLoading={loading}
          disabled={loading}
          onClick={handleGoogleLogin}
          hoverColor="#9404C3"
          data-google-login="true"
          style={{ marginTop: '-20px' }}
        >
          {loading ? 'Redirecting...' : 'Continue with Google'}
        </NeumorphicButtonWithIcon> */}
      </form>

    </div>
  );
});

LoginForm.displayName = 'LoginForm';

LoginForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onOrgSuspended: PropTypes.func,
};

const arePropsEqual = (prevProps, nextProps) => {
  return prevProps.onSubmit === nextProps.onSubmit;
};

export default memo(LoginForm, arePropsEqual);